/**
 * New Delivery Request Page - Transport Request Form with Auto-Fill
 * Form for creating a new delivery with auto-fill from order details
 * Used by UrjaVyapar module to create deliveries
 * @author SAUBHAGYA Development Team
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ChevronLeft, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { createDelivery } from '@/services/commerce/deliveryService';
import orderService from '@/services/commerce/orderService';
import type { OrderDetails } from '@/types/order';
import { AutoFillNotification } from './components/AutoFillNotification';
import { mapOrderToDeliveryForm, validateMappedData } from './utils/orderToDeliveryMapper';
import { useDeliveryForm } from './hooks/useDeliveryForm';

interface TransportDeliveryFormData {
  // Pickup Details
  pickupAddress: string;
  pickupLatitude?: number;
  pickupLongitude?: number;
  pickupContactName: string;
  pickupContactPhone: string;
  requestedPickupTime?: string;

  // Drop Details
  dropAddress: string;
  dropLatitude?: number;
  dropLongitude?: number;
  dropContactName: string;
  dropContactPhone: string;

  // Product Details
  productType: string;
  quantity: number;
  productDescription?: string;
  unit: 'Kilograms' | 'Liters' | 'Units' | 'Boxes';
  weightKg: number;

  // Other Details
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  estimatedAmount?: number;
  markAsUrgent: boolean;
  additionalNotes?: string;

  // Source tracking
  assignedFrom: 'UrjaVyapar';
}

interface FieldErrors {
  [key: string]: string | undefined;
}

export default function NewDeliveryRequest() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get orderId from URL params
  const orderId = searchParams.get('orderId');
  const invoiceId = searchParams.get('invoiceId');

  // State management
  const {
    formData,
    autoFilledFields,
    isAutoFilled,
    updateField,
    updateMultipleFields,
    setAutoFilledData,
  } = useDeliveryForm();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [success, setSuccess] = useState(false);
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [fetchingOrder, setFetchingOrder] = useState(false);

  /**
   * Fetch and auto-fill order details on component mount
   * Only runs once when orderId changes
   */
  useEffect(() => {
    if (!orderId) {
      console.log('[NewDeliveryRequest] No orderId in URL params');
      return;
    }

    const fetchAndAutoFill = async () => {
      try {
        setFetchingOrder(true);
        console.log('[NewDeliveryRequest] Fetching order:', orderId);

        const orderData = await orderService.getOrderById(parseInt(orderId, 10));
        setOrder(orderData);

        // Map order to delivery form
        const mappedData = mapOrderToDeliveryForm(orderData);

        // Validate mapped data
        const validation = validateMappedData(mappedData);
        if (!validation.isValid) {
          console.warn('[NewDeliveryRequest] Missing fields:', validation.missingFields);
        }

        // Set auto-filled data
        setAutoFilledData(mappedData, formData);

        console.log('[NewDeliveryRequest] Auto-fill completed successfully');

        // Show success notification only once after successful load
        setTimeout(() => {
          toast.success('Order Details Loaded', {
            description: `Form auto-filled from Order #${orderData.orderNumber}`,
          });
        }, 500);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch order';
        console.error('[NewDeliveryRequest] Error fetching order:', err);
        setError(errorMessage);

        toast.error('Failed to Load Order', {
          description: errorMessage,
        });
      } finally {
        setFetchingOrder(false);
      }
    };

    fetchAndAutoFill();
    // IMPORTANT: Only depend on orderId, not formData or setAutoFilledData
    // This ensures the effect runs only once when orderId changes
  }, [orderId]);

  /**
   * Handle input change for form fields
   */
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const fieldName = name as keyof typeof formData;

    updateField(
      fieldName,
      type === 'checkbox'
        ? (e.target as HTMLInputElement).checked
        : type === 'number'
          ? parseFloat(value) || 0
          : value
    );
  };

  const parseValidationErrors = (errorMessage: string): FieldErrors => {
    const errors: FieldErrors = {};
    const errorParts = errorMessage.split('; ');

    errorParts.forEach((part) => {
      const match = part.match(/^(\w+):\s*(.+)$/);
      if (match) {
        const [, field, message] = match;
        errors[field] = message;
      }
    });

    return errors;
  };

  const validateForm = (): boolean => {
    setFieldErrors({});

    // Validate Order ID is present
    if (!orderId || isNaN(parseInt(orderId, 10))) {
      setError('Please select an order first. Go back to the order list and click the truck icon for a specific order.');
      return false;
    }

    // Only validate REQUIRED fields
    if (!formData.dropAddress?.trim()) {
      setError('Delivery Address is required');
      return false;
    }
    if (!formData.dropContactName?.trim()) {
      setError('Contact Person Name is required');
      return false;
    }
    if (!formData.dropContactPhone?.trim()) {
      setError('Contact Phone is required');
      return false;
    }
    if (!/^\d{10}$/.test(formData.dropContactPhone.replace(/\D/g, ''))) {
      setError('Contact Phone must be a valid 10-digit number');
      return false;
    }

    return true;
  };

  /**
   * Handle form submission with order linkage and status update
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      console.log('[NewDeliveryRequest] Submitting delivery form...');

      // Send all fields to backend
      const payload = {
        // Use salesOrderId to link delivery directly to the sales order
        // This is the preferred method for UrjaVyapar deliveries
        salesOrderId: orderId ? parseInt(orderId, 10) : null,
        // batchAllocationId is optional and not required for UrjaVyapar deliveries
        batchAllocationId: null,
        status: 'PENDING',

        // Pickup Details
        pickupAddress: formData.pickupAddress,
        pickupContactName: formData.pickupContactName,
        pickupContactPhone: formData.pickupContactPhone,

        // Drop/Delivery Details
        deliveryAddress: formData.dropAddress,
        dropLatitude: formData.dropLatitude,
        dropLongitude: formData.dropLongitude,
        dropContactName: formData.dropContactName,
        dropContactPhone: formData.dropContactPhone,

        // Product Details
        productType: formData.productType,
        quantity: formData.quantity,
        productDescription: formData.productDescription,
        unit: formData.unit,
        weightKg: formData.weightKg,

        // Other Details
        priority: formData.priority,
        estimatedAmount: formData.estimatedAmount,
        additionalNotes: formData.additionalNotes,

        // Customer name from order
        customerName: order?.customer?.customerName || formData.dropContactName || '',

        // Source tracking
        assignedFrom: 'UrjaVyapar',
      };

      console.log('[NewDeliveryRequest] Full Payload:', payload);

      // Step 1: Create delivery
      const createdDelivery = await createDelivery(payload as any, 'UrjaVyapar');
      console.log('[NewDeliveryRequest] Delivery created successfully:', createdDelivery);

      // Step 2: Update order status if orderId is available
      if (orderId) {
        const orderIdNum = parseInt(orderId, 10);
        try {
          console.log('[NewDeliveryRequest] Updating order status to IN_TRANSIT...');
          await orderService.updateOrder(orderIdNum, {
            status: 'IN_TRANSIT',
          });
          console.log('[NewDeliveryRequest] Order status updated successfully');
        } catch (statusUpdateError) {
          console.warn('[NewDeliveryRequest] Warning: Could not update order status:', statusUpdateError);
          // Don't fail the entire operation if status update fails
          // The delivery was created successfully
        }
      }

      setSuccess(true);

      toast.success('Delivery Created Successfully', {
        description: `Delivery created and linked to Order #${order?.orderNumber || orderId}. Order status updated to In Transit.`,
      });

      // Redirect after success
      setTimeout(() => {
        navigate('/sales/delivery', { state: { refresh: true } });
      }, 2000);
    } catch (err: any) {
      console.error('Failed to create delivery:', err);
      setError(err.message || 'Failed to create delivery');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      navigate('/sales/delivery');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header with Back Button */}
        <div className="mb-8 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {orderId ? 'Create Delivery from Order' : 'Create Transport Request'}
            </h1>
            <p className="text-gray-600 mt-1">
              {orderId && order
                ? `Order #${order.orderNumber} - Form auto-filled from order details`
                : 'Create a new delivery request in the system'}
            </p>
          </div>
        </div>

        {/* Loading State */}
        {fetchingOrder && (
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <CardContent className="flex items-center gap-3 py-6">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <p className="text-blue-800 font-medium">Loading order details and auto-filling form...</p>
            </CardContent>
          </Card>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Auto-Fill Notification */}
          {isAutoFilled && order && (
            <AutoFillNotification
              orderNumber={order.orderNumber}
              orderId={orderId ? parseInt(orderId, 10) : 0}
              autoFilledFields={autoFilledFields}
              onViewOrder={() => {
                // Could open a modal or navigate to order details
                toast.info('Order Details', {
                  description: `Order #${order.orderNumber}`,
                });
              }}
            />
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert className="bg-green-50 border-green-200">
              <AlertCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                ✓ Delivery created successfully! Redirecting...
              </AlertDescription>
            </Alert>
          )}

          {/* Order Info Section */}
          {order && (
            <Card className="bg-blue-50 border-blue-200 mb-6">
              <CardHeader>
                <CardTitle>Order Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm">
                  <span className="font-semibold">Order Number:</span> {order.orderNumber}
                </p>
                <p className="text-sm">
                  <span className="font-semibold">Customer:</span> {order.customer?.customerName}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Pickup Details Section */}
          <Card>
            <CardHeader>
              <CardTitle>Pickup Details</CardTitle>
              <CardDescription>Where and when to pick up the product</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pickup Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pickup Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="pickupAddress"
                  value={formData.pickupAddress}
                  onChange={handleInputChange}
                  placeholder="Enter complete pickup address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              {/* Latitude & Longitude Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                  <input
                    type="number"
                    name="pickupLatitude"
                    value={formData.pickupLatitude || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., 28.7041"
                    step="0.00001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                  <input
                    type="number"
                    name="pickupLongitude"
                    value={formData.pickupLongitude || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., 77.1025"
                    step="0.00001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Contact Name & Phone Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="pickupContactName"
                    value={formData.pickupContactName}
                    onChange={handleInputChange}
                    placeholder="Contact person name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone <span className="text-gray-400 text-xs">(Optional)</span>
                  </label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    name="pickupContactPhone"
                    value={formData.pickupContactPhone}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      handleInputChange({ ...e, target: { ...e.target, name: 'pickupContactPhone', value: digits, type: 'text' } } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    placeholder="10-digit phone number (optional)"
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Drop/Delivery Details Section */}
          <Card>
            <CardHeader>
              <CardTitle>Delivery Details</CardTitle>
              <CardDescription>Where to deliver the product</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Drop Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="dropAddress"
                  value={formData.dropAddress}
                  onChange={handleInputChange}
                  placeholder="Enter complete delivery address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              {/* Latitude & Longitude Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                  <input
                    type="number"
                    name="dropLatitude"
                    value={formData.dropLatitude || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., 28.5355"
                    step="0.00001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                  <input
                    type="number"
                    name="dropLongitude"
                    value={formData.dropLongitude || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., 77.3910"
                    step="0.00001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Contact Name & Phone Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="dropContactName"
                    value={formData.dropContactName}
                    onChange={handleInputChange}
                    placeholder="Recipient name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    name="dropContactPhone"
                    value={formData.dropContactPhone}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      handleInputChange({ ...e, target: { ...e.target, name: 'dropContactPhone', value: digits, type: 'text' } } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    placeholder="10-digit phone number"
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Product Details Section */}
          <Card>
            <CardHeader>
              <CardTitle>Product Details</CardTitle>
              <CardDescription>What is being delivered</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Product Type & Quantity Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="productType"
                    value={formData.productType}
                    onChange={handleInputChange}
                    placeholder="e.g., Biogas, CBG, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., 50"
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Unit & Weight Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Kilograms">Kilograms (kg)</option>
                    <option value="Liters">Liters</option>
                    <option value="Units">Units</option>
                    <option value="Boxes">Boxes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weight (kg) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="weightKg"
                    value={formData.weightKg || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., 100"
                    min="1"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Product Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Description</label>
                <textarea
                  name="productDescription"
                  value={formData.productDescription || ''}
                  onChange={handleInputChange}
                  placeholder="Additional details about the product"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Other Details Section */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Details</CardTitle>
              <CardDescription>Priority and notes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Priority & Estimated Amount Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Amount</label>
                  <input
                    type="number"
                    name="estimatedAmount"
                    value={formData.estimatedAmount || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., 5000"
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                <textarea
                  name="additionalNotes"
                  value={formData.additionalNotes || ''}
                  onChange={handleInputChange}
                  placeholder="Any special instructions or notes"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              className="px-6 py-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || success}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? 'Creating...' : 'Create Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
